import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Handshake,
  Mic,
  Building2,
  Users,
  Briefcase,
  BookOpenCheck,
  Landmark,
  ShieldCheck,
  Network,
  School,
  BadgeCheck,
  CalendarClock,
} from "lucide-react";

const inert = (e) => e.preventDefault();

const RESOURCE_THUMBS = [
  "/resource-guide.jpg",
  "/resource-checklist.jpg",
  "/resource-glossary.jpg",
];

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Hairline dividers between blocks removed per client request */

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* Section numbering (line + number) removed per client request */

/* ===== Block 1 — Hero: editorial magazine-cover composition ===== */

const HERO_PANORAMA = "/hero-panorama-2.jpg";

const Hero = () => (
  <section
    data-testid="hero-section"
    className="lg:flex lg:min-h-[calc(100vh-65px)] lg:flex-col lg:justify-center lg:mb-[-100px]"
  >
    <Container className="pt-[41px] sm:pt-[49px] lg:py-[90px]">
      <div className="grid gap-[35px] sm:gap-[43px] lg:grid-cols-[618px_1fr] lg:items-center lg:gap-3">
        {/* Left — editorial text */}
        <div className="mx-auto max-w-5xl text-center lg:mx-0 lg:max-w-none lg:text-left">
          <div className="flex items-center justify-center gap-3 lg:justify-start">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
              World Financial Innovation Institute
            </span>
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))] lg:hidden" />
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 lg:hidden" />
          </div>
          <h1 className="font-serif-editorial mx-auto mt-8 text-[2.75rem] font-semibold leading-[1.06] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-6xl lg:mx-0 lg:mt-[52px] lg:text-[4.4rem]">
            Building Financial Confidence for the Digital Economy
          </h1>
          <p className="mx-auto mt-8 max-w-[62ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0 lg:mt-[52px] lg:max-w-[560px] lg:text-xl">
            Preparing finance professionals for a world where money,
            technology, and business are becoming more connected. WFII
            provides the knowledge to understand, adapt, and lead in the
            evolving landscape of digital finance.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 lg:mt-[56px] lg:justify-start">
            <Button
              asChild
              className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
              data-testid="hero-primary-cta-button"
            >
              <Link to="/programs">Explore Programs</Link>
            </Button>
            <Link
              to="/enrollment-request" state={{ type: "membership" }}
              className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md lg:ml-[15px]"
              data-testid="hero-secondary-cta-button"
            >
              Become a Member
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                strokeWidth={1.75}
              />
            </Link>
          </div>
        </div>

        {/* Right — editorial image */}
        <div className="overflow-hidden rounded-2xl shadow-[0_40px_80px_-40px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10">
          <img
            src={HERO_PANORAMA}
            alt="Finance professionals in a working roundtable discussion"
            className="wfii-photo aspect-[16/10] w-full object-cover object-[center_20%] sm:aspect-[21/9] lg:aspect-[3/2]"
            loading="eager"
            data-testid="hero-image"
          />
        </div>
      </div>
    </Container>
  </section>
);

/* ============ Block 2 — Pillars: numbered editorial columns ============ */

const PILLARS = [
  {
    n: "01",
    title: "Learn",
    body: "Programs on digital money, stablecoins, digital assets, and financial innovation.",
  },
  {
    n: "02",
    title: "Get Certified",
    body: "Credentials covering stablecoins, payments, reporting, accounting, audit, and risk management.",
  },
  {
    n: "03",
    title: "Stay Informed",
    body: "Guides, reports, and frameworks for more informed financial decisions.",
    bodyJsx: (
      <>
        {"Guides, reports, and frameworks"}
        <br className="hidden lg:block" />
        {" for more informed financial"}
        <br className="hidden lg:block" />
        {" decisions."}
      </>
    ),
  },
  {
    n: "04",
    title: "Connect",
    body: (
      <>
        A network connecting finance professionals{" "}
        <br className="sm:hidden" aria-hidden="true" />
        and organizations advancing digital finance.
      </>
    ),
  },
];

const Pillars = () => (
  <section data-testid="pillars-section">
    <Container className="">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        Education. Research. Community.
      </h2>
      <div className="mt-[46px] grid gap-x-10 gap-y-12 sm:mt-14 sm:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map((p) => (
          <div
            key={p.title}
            className="group border-t border-[hsl(var(--accent))] pt-6"
            data-testid={`pillars-${p.title.toLowerCase().replace(/\s+/g, "-")}-item`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium tracking-[0.08em] text-muted-foreground">
                {p.n}
              </span>
              <span
                className="h-2 w-2 rounded-[2px] bg-[hsl(var(--accent))]/0 transition-colors group-hover:bg-[hsl(var(--accent))]/80"
                aria-hidden="true"
              />
            </div>
            <h3 className="mt-4 text-[20px] font-semibold text-foreground">{p.title}</h3>
            <p className="mt-3 text-[16px] leading-relaxed text-muted-foreground">{p.bodyJsx || p.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ========= Block 3 — Featured Programs: four identical photo cards ========= */

const PROGRAMS = [
  {
    title: "Stablecoins for Business Finance Teams",
    body: "Discover how stablecoins support business payments, payouts, settlements, and financial operations.",
    img: "/program-stablecoins.jpg",
    alt: "Conference audience at a business finance industry event",
    testId: "featured-programs-featured-card",
    href: "/programs/digital-money-and-stablecoins",
  },
  {
    title: "Digital Asset Accounting Fundamentals",
    body: (<>Learn how to record, reconcile, and report digital asset activity with greater clarity{" "}<br className="hidden lg:block" />and structure.</>),
    img: "/program-accounting-3.jpg",
    alt: "Modern financial district office tower at night",
    testId: "featured-programs-program-row-1",
    href: "/programs/digital-asset-accounting",
  },
  {
    title: "Digital Money & Business Payments",
    body: "Explore how digital money is changing cross-border payments, payouts, settlements, treasury operations, and business finance workflows.",
    img: "/program-payments.jpg",
    alt: "Digital payment on a laptop",
    testId: "featured-programs-program-row-2",
    href: "/programs/business-payments-and-treasury",
  },
  {
    title: "Compliance for Digital Finance Operations",
    body: "Understand the core controls behind digital finance operations, including wallet screening, internal policies, risk management, and operational governance.",
    img: "/program-compliance.jpg",
    alt: "Panel discussion on governance, risk, and digital finance compliance",
    testId: "featured-programs-program-row-3",
    href: "/programs/compliance-for-digital-finance-operations",
  },
  {
    title: (<>Digital Asset Audit{" "}<br className="hidden lg:block" />&amp; Assurance</>),
    body: "Learn how to evaluate digital asset transactions, records, balances, valuation, supporting evidence, and internal controls.",
    img: "/program-audit-assurance.jpg",
    alt: "Auditor reviewing digital asset transaction records",
    testId: "featured-programs-program-row-4",
    href: "/programs/digital-asset-audit-and-assurance",
    fullWidth: true,
  },
];

const FeaturedPrograms = () => (
  <section data-testid="featured-programs-section">
    <Container className="">
      <h2 className="text-center text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        Upcoming Programs
      </h2>
      <div
        className="mt-[14px] flex w-full flex-col items-center gap-2 px-0 pt-4 pb-[40px] sm:flex-row sm:items-center sm:gap-6 sm:overflow-x-auto sm:px-8 sm:mt-7 sm:pb-[55px] sm:[mask-image:none] lg:mx-auto lg:w-fit lg:overflow-visible"
        data-testid="featured-programs-enrollment-banner"
      >
        <span className="flex shrink-0 items-center gap-2.5 whitespace-nowrap text-sm font-medium text-foreground sm:text-base lg:whitespace-normal">
          <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--accent))] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--accent))]" />
          </span>
          Programs begin in January 2027
        </span>
        <span className="hidden h-5 w-px shrink-0 bg-[hsl(var(--accent))]/25 sm:block" aria-hidden="true" />
        <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium text-[hsl(var(--accent))] sm:text-base lg:whitespace-normal">
          <CalendarClock className="h-4 w-4 shrink-0" strokeWidth={1.9} />
          Applications are open until December 23, 2026
        </span>
      </div>
      <div className="mt-0 grid gap-6 md:grid-cols-2 lg:flex lg:flex-wrap lg:justify-center lg:gap-8">
        {PROGRAMS.map((p) => {
          const Tag = p.href ? Link : "div";
          const tagProps = p.href ? { to: p.href } : {};
          return (
          <Tag
            key={p.testId}
            {...tagProps}
            className={`group block overflow-hidden rounded-2xl bg-[hsl(174_45%_40%/0.06)] shadow-[0_24px_48px_-20px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/5 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${p.href ? "hover:shadow-[0_32px_56px_-20px_hsl(218_51%_15%/0.38)]" : ""} ${p.fullWidth ? "md:col-span-2" : ""} lg:w-[calc((100%-64px)/3)]`}
            data-testid={p.testId}
          >
            <div className={`px-[24px] pt-6 pb-6 sm:px-[33px] sm:pt-7 sm:pb-7 lg:px-[28px] lg:pt-[23px] lg:pb-[23px]`}>
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={p.img}
                  alt={p.alt}
                  className="wfii-photo aspect-[400/188] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] sm:aspect-[400/171]"
                  loading="lazy"
                />
                <span className="absolute bottom-0 left-0 h-1 w-full bg-[hsl(var(--accent))]" aria-hidden="true" />
              </div>
            </div>
            <div className={`px-[24px] pb-6 sm:px-[33px] sm:pb-7 lg:px-[28px] lg:pb-[23px]`}>
              <div className="flex items-start justify-between gap-6">
                <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground sm:text-2xl">
                  {p.title}
                </h3>
                <ArrowUpRight
                  className={`mt-1 h-5 w-5 shrink-0 transition-colors ${p.href ? "text-muted-foreground group-hover:text-[hsl(var(--accent))]" : "text-muted-foreground/50"}`}
                  strokeWidth={1.75}
                />
              </div>
              <p className="mt-2.5 max-w-none text-sm leading-relaxed text-muted-foreground sm:max-w-[60ch] sm:text-base">
                {p.body}
              </p>
            </div>
          </Tag>
          );
        })}
      </div>
    </Container>
  </section>
);

/* ====== Block 4 — Editorial statement: navy panel (matches final CTA) ====== */

const Statement = () => (
  <section data-testid="statement-section">
    <Container className="">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-14">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-40 -top-48 h-[460px] w-[460px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-8 right-8 hidden sm:flex" />
        <div className="relative grid gap-10 lg:grid-cols-[560px_1fr]">
          <div>
            <span className="block h-[3px] w-12 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <h2 className="font-serif-editorial mt-8 text-3xl font-semibold leading-[1.12] tracking-[-0.02em] [text-wrap:balance] sm:text-4xl lg:text-5xl">
              Finance Is Changing.{" "}
              <br className="lg:hidden" aria-hidden="true" />
              Education Must{" "}
              <br className="lg:hidden" aria-hidden="true" />
              Catch Up.
            </h2>
          </div>
          <div className="lg:pt-2">
            <div className="border-l-2 border-[hsl(var(--accent))]/70 pl-6 sm:pl-8">
              <p className="text-lg leading-relaxed text-primary-foreground/95 sm:text-xl sm:leading-[1.5]">
                Stablecoins, tokenized assets,{" "}
                <br className="sm:hidden" aria-hidden="true" />
                AI-supported finance workflows, and
                digital payment rails are entering business operations faster than
                many teams are prepared for.
              </p>
              <p className="mt-6 text-base leading-relaxed text-primary-foreground/70 sm:text-lg lg:text-base">
                WFII helps finance professionals close that knowledge gap with
                clear education, frameworks, and responsible guidance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ====== Block 5 — Audiences: sticky intro panel + scrolling numbered cards ====== */

const AUDIENCE_IMAGE = "/audience-teams.jpg";

const AUDIENCES = [
  {
    icon: Briefcase,
    title: "CFOs",
    body: "Understand the opportunities, risks, controls, and operational requirements behind digital finance adoption.",
    id: "cfos",
  },
  {
    icon: BookOpenCheck,
    title: "Accountants & Bookkeepers",
    body: "Learn how stablecoins and digital assets affect recording, reconciliation, reporting, receipts, and client workflows.",
    id: "accountants-bookkeepers",
  },
  {
    icon: Landmark,
    title: "Treasury Teams",
    body: "Explore how digital money can affect settlement, liquidity, payout operations, and cross-border payment processes.",
    id: "treasury-teams",
  },
  {
    icon: ShieldCheck,
    title: "Compliance Teams",
    body: "Understand policies, wallet risk, sanctions screening, transaction monitoring, internal controls, and governance.",
    id: "compliance-teams",
  },
  {
    icon: BadgeCheck,
    title: "Auditors & Assurance Professionals",
    body: "Evaluate digital asset records, transaction evidence, balances, valuation, controls, and reporting with greater confidence.",
    id: "auditors-assurance-professionals",
  },
  {
    icon: Network,
    title: "Technology & Payment Companies",
    body: "Support market education and help business clients adopt digital finance with greater clarity and responsibility.",
    id: "technology-payment-companies",
  },
  {
    icon: School,
    title: "Educators & Institutions",
    body: "Bring digital finance literacy into professional education, continuing education, and industry training.",
    id: "educators-institutions",
  },
];

const Audiences = () => (
  <section data-testid="audiences-section">
    <Container className="">
      <div className="grid gap-8 lg:grid-cols-[588px_1fr] lg:gap-10">
        {/* Left — sticky title and photo */}
        <div>
          <div className="lg:sticky lg:top-24">
            <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
              For the Teams Behind Business Finance
            </h2>
            <div className="mt-8 overflow-hidden rounded-2xl lg:mt-[47px] lg:w-[90%]">
              <img
                src={AUDIENCE_IMAGE}
                alt="Finance professionals reviewing financial records"
                className="wfii-photo aspect-[4/3] w-full object-cover lg:aspect-[529.1875/348.2015625]"
                loading="lazy"
                data-testid="audiences-image"
              />
            </div>
          </div>
        </div>

        {/* Right — scrolling cards */}
        <div className="space-y-6">
          {AUDIENCES.map((a) => (
            <div
              key={a.id}
              className="rounded-2xl border border-[hsl(var(--primary))]/12 bg-card p-7 transition-shadow hover:shadow-[0_24px_48px_-28px_hsl(218_51%_15%/0.35)] sm:p-9"
              data-testid={`audiences-row-${a.id}`}
            >
              <div className="flex items-center gap-4">
                <a.icon
                  className="h-5 w-5 shrink-0 text-[hsl(var(--wfii-teal))]"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <h3 className="text-lg font-semibold tracking-[-0.01em] text-foreground sm:text-xl">
                  {a.title}
                </h3>
              </div>
              <p className="mt-5 max-w-[60ch] text-sm leading-relaxed text-muted-foreground sm:text-base">
                {a.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ========= Block 6 — Partner band: restrained navy panel ========= */

const PartnerBand = () => (
  <section data-testid="partner-band-section">
    <Container className="">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-8 text-primary-foreground shadow-[0_32px_64px_-28px_hsl(218_51%_15%/0.5)] sm:p-12 lg:p-14">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-8 right-8 hidden sm:flex" />
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl lg:max-w-[47rem]">
            <span className="block h-[3px] w-12 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <h2 className="mt-6 text-3xl font-semibold tracking-[-0.02em] sm:text-4xl lg:text-5xl">
              Advancing Digital Finance
              <br className="hidden lg:block" /> Education Together
            </h2>
            <p className="mt-4 max-w-[58ch] text-sm leading-relaxed text-primary-foreground/75 sm:text-base lg:max-w-none">
              WFII connects industry expertise with professional education,
              helping finance teams understand digital money, stablecoins,
              payments, compliance, and business adoption with confidence.
            </p>
          </div>
          <Button
            className="h-12 shrink-0 bg-primary-foreground px-7 text-sm font-medium text-primary hover:bg-primary-foreground/90 active:scale-[0.98]"
            asChild
            data-testid="knowledge-partner-cta-button"
          >
            <Link to="/partners">Become a Knowledge Partner</Link>
          </Button>
        </div>
      </div>
    </Container>
  </section>
);

/* ====== Block 7 — Research & Guides: vertical course-style cards ====== */

const RESOURCES = [
  {
    title: "Stablecoin Readiness Guide for CFOs",
    titleJsx: (<>Stablecoin Readiness Guide{" "}<br className="sm:hidden" aria-hidden="true" />for CFOs</>),
    meta: "Guide",
    desc: "Stablecoins are finding a place in business finance, particularly in cross-border payments and global payout workflows. For finance teams, understanding how they work is the first step toward evaluating where they fit within existing financial operations.",
    href: "/resources/understanding-stablecoins-in-business-finance",
  },
  {
    title: "Digital Asset Accounting Checklist",
    meta: "Guide",
    desc: "A company sends 5,000 USDC to an international contractor. Within seconds, the transaction appears on the blockchain. The payment has a timestamp, a transaction ID, a sender address, and a recipient address.",
    href: "/resources/digital-asset-records-and-reporting",
  },
  {
    title: "Stablecoin Payments Glossary",
    meta: "Glossary",
    desc: "Digital finance introduces new terminology that finance professionals, accountants, and business leaders are increasingly encountering in conversations about payments, accounting, and financial operations.",
    href: "/resources/glossary-25-essential-digital-finance-terms",
  },
];

const ResearchGuides = () => (
  <section data-testid="research-guides-section">
    <Container className="">
      {/* Header: centered heading with subheading below */}
      <div className="text-center">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Research &amp; Guides
        </h2>
      </div>

      {/* Vertical cards */}
      <div className="mt-[28px] grid gap-6 sm:mt-12 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
        {RESOURCES.map((r, i) => (
          <Link
            key={r.title}
            to={r.href}
            className="group flex flex-col overflow-hidden rounded-[20px] border border-[hsl(var(--primary))]/12 bg-card transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`research-resource-item-${i + 1}`}
          >
            {/* Card artwork — natural photo, no filters */}
            <span className="relative block overflow-hidden">
              <span className="block px-[25px] pt-[25px] sm:px-[29px] sm:pt-[29px]">
                <span className="relative block overflow-hidden rounded-[20px]">
                  <img
                    src={RESOURCE_THUMBS[i]}
                    alt=""
                    className="aspect-[32/17] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </span>
              </span>
            </span>

            {/* Body */}
            <span className="flex flex-1 flex-col p-[25px] sm:p-[29px]">
              <span className="text-xl font-semibold leading-snug tracking-[-0.01em] text-foreground transition-colors group-hover:text-[hsl(var(--wfii-teal))] sm:text-[1.35rem] lg:text-lg lg:whitespace-nowrap">
                {r.titleJsx || r.title}
              </span>
              <span className="mt-8 flex flex-1 items-end justify-end sm:mt-10">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {r.meta}
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={1.75}
                  />
                </span>
              </span>
            </span>
          </Link>
        ))}
      </div>
    </Container>
  </section>
);

/* ========= Block 8 — Membership: tier columns, navy featured tier ========= */

const Membership = () => (
  <section data-testid="membership-section">
    <Container className="">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
            Membership
          </h2>
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-muted-foreground">
            Connect with professionals and organizations building knowledge
            around modern finance.
          </p>
        </div>
        <Button
          asChild
          variant="outline"
          className="hidden h-11 shrink-0 border-[hsl(var(--primary))]/25 bg-card px-6 text-sm font-medium active:scale-[0.98] sm:inline-flex"
          data-testid="membership-learn-more-button"
        >
          <Link to="/membership">Learn more</Link>
        </Button>
      </div>
      <div className="mt-[31px] grid gap-6 sm:mt-14 lg:grid-cols-3">
        {/* Individual */}
        <div
          className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-9"
          data-testid="membership-tier-individual"
        >
          <span className="block h-[3px] w-10 bg-[hsl(var(--primary))]/20" aria-hidden="true" />
          <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-foreground">
            Individual Member
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
            For finance professionals, accountants, bookkeepers, CFOs, and
            students.
          </p>
        </div>
        {/* Corporate — featured navy tier */}
        <div
          className="wfii-stipple-dark relative overflow-hidden rounded-2xl bg-primary p-7 text-primary-foreground shadow-[0_32px_64px_-24px_hsl(218_51%_15%/0.55)] sm:p-9 lg:-my-3 lg:py-12"
          data-testid="membership-tier-corporate"
        >
          <div className="relative">
            <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em]">Corporate Member</h3>
            <p className="mt-3 text-sm leading-relaxed text-primary-foreground/75 sm:text-base">
              For companies training teams and preparing for modern finance
              workflows.
            </p>
          </div>
        </div>
        {/* Knowledge Partner */}
        <div
          className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-9"
          data-testid="membership-tier-knowledge-partner"
        >
          <span className="block h-[3px] w-10 bg-[hsl(var(--primary))]/20" aria-hidden="true" />
          <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-foreground">
            Knowledge Partner
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
            For organizations contributing expertise, research, speakers, or
            sponsorship.
          </p>
        </div>
      </div>
      <Button
        asChild
        variant="outline"
        className="mb-[5px] mt-8 h-11 w-full border-[hsl(var(--primary))]/25 bg-card px-6 text-sm font-medium active:scale-[0.98] sm:hidden"
        data-testid="membership-learn-more-button-mobile"
      >
        <Link to="/membership">Learn more</Link>
      </Button>
    </Container>
  </section>
);

/* ====== Block 9 — Industry expertise: divided strip ====== */

const EXPERTISE = [
  { icon: Handshake, title: "Founding Partners", id: "founding-partners" },
  { icon: Mic, title: "Speakers", id: "speakers" },
  { icon: Building2, title: "Supporting Organizations", id: "supporting-organizations" },
  { icon: Users, title: "Featured Contributors", id: "featured-contributors" },
];

const IndustryExpertise = () => (
  <section data-testid="industry-expertise-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h2 className="mt-7 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Built With Industry Expertise
        </h2>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          WFII brings together advisors, contributors, speakers, and partner
          organizations with experience across finance, accounting, payments,
          compliance, technology, and education.
        </p>
      </div>
      <div className="mt-[31px] grid grid-cols-2 overflow-hidden rounded-2xl shadow-[0_16px_40px_-28px_hsl(218_51%_15%/0.35)] ring-1 ring-[hsl(var(--primary))]/10 sm:mt-14 md:grid-cols-4">
        {EXPERTISE.map((t, i) => (
          <div
            key={t.id}
            className={`flex flex-col items-center gap-4 bg-card/70 px-6 py-12 text-center backdrop-blur-[2px] transition-colors hover:bg-card ${
              i % 2 === 1 ? "border-l border-[hsl(var(--primary))]/10" : ""
            } ${i >= 2 ? "border-t border-[hsl(var(--primary))]/10 md:border-t-0" : ""} ${
              i >= 1 ? "md:border-l md:border-[hsl(var(--primary))]/10" : ""
            }`}
            data-testid={`expertise-item-${t.id}`}
          >
            <t.icon className="h-6 w-6 text-[hsl(var(--wfii-teal))]" strokeWidth={1.5} />
            <span className="text-sm font-semibold text-foreground sm:text-base">{t.title}</span>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ========= Block 10 — Final CTA: navy editorial panel ========= */

const FinalCta = () => (
  <section data-testid="final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary px-8 py-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:whitespace-nowrap lg:text-5xl">
            Shape the Future of Finance
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-[15px] leading-relaxed text-primary-foreground/75 sm:text-lg">
              The future of finance requires new thinking. WFII helps
              professionals build{" "}
              <br className="hidden lg:block" />
              the knowledge to lead it.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 w-full bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98] sm:w-auto"
                data-testid="final-cta-become-member-button"
              >
                <Link to="/enrollment-request" state={{ type: "membership" }}>Become a Member</Link>
              </Button>
              <Button
                variant="outline"
                className="h-12 w-full border-primary-foreground/30 bg-transparent px-7 text-sm font-medium text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground active:scale-[0.98] sm:w-auto"
                asChild
                data-testid="final-cta-partner-button"
              >
                <Link to="/partners">Partner With Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================================ Page ================================ */

export default function HomePage() {
  return (
    <div className="relative min-h-screen">
      {/* One continuous background canvas — fixed, content scrolls over it */}
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[130px]">
          <Hero />
          <Pillars />
          <FeaturedPrograms />
          <Statement />
          <Audiences />
          <PartnerBand />
          <ResearchGuides />
          <Membership />
          <IndustryExpertise />
          <FinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
